import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { zohoAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, RefreshCw, FlaskConical } from 'lucide-react';
import ZohoReportTable from '@/components/zoho/ZohoReportTable';
import ZohoReportFilters from '@/components/zoho/ZohoReportFilters';
import ZohoReportColumnPicker from '@/components/zoho/ZohoReportColumnPicker';
import ZohoReportExport from '@/components/zoho/ZohoReportExport';
import ZohoReportViewManager from '@/components/zoho/ZohoReportViewManager';

export default function ZohoFormReport({ form }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [excludeTest, setExcludeTest] = useState(true);
  const [columns, setColumns] = useState(form?.column_config?.length ? form.column_config : (form?.detected_fields?.slice(0, 6) || []));
  const [filterFields, setFilterFields] = useState([]);
  const [filters, setFilters] = useState({});
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');

  const allFields = form?.detected_fields || [];

  const fetchSubmissions = useCallback(async () => {
    if (!form?.id) return;
    setLoading(true);
    try {
      const res = await zohoAPI.getFormSubmissions(form.id, { limit: 500, exclude_test: excludeTest });
      setSubmissions(res.data.submissions || []);
    } catch { setSubmissions([]); }
    setLoading(false);
  }, [form?.id, excludeTest]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  // Client-side filters
  const filteredSubs = submissions.filter(s => {
    for (const [field, value] of Object.entries(filters)) {
      if (!value) continue;
      const fieldVal = String((s.raw_payload || {})[field] || '').toLowerCase();
      if (!fieldVal.includes(value.toLowerCase())) return false;
    }
    return true;
  });

  // Client-side sort
  const sortedSubs = [...filteredSubs].sort((a, b) => {
    if (!sortField) return 0;
    const aVal = String((a.raw_payload || {})[sortField] || '');
    const bVal = String((b.raw_payload || {})[sortField] || '');
    const numA = parseFloat(aVal), numB = parseFloat(bVal);
    if (!isNaN(numA) && !isNaN(numB)) {
      return sortOrder === 'asc' ? numA - numB : numB - numA;
    }
    return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const loadView = (view) => {
    setColumns(view.columns || []);
    setFilterFields(view.filter_fields || []);
    setFilters(view.filters || {});
    setSortField(view.sort_field || null);
    setSortOrder(view.sort_order || 'desc');
  };

  const getCurrentView = () => ({
    columns,
    filter_fields: filterFields,
    filters,
    sort_field: sortField,
    sort_order: sortOrder,
  });

  const handleColumnsChange = async (newCols) => {
    setColumns(newCols);
    try { await zohoAPI.saveColumnConfig(form.id, newCols); } catch {}
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="form-report-section">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button
            variant={excludeTest ? 'default' : 'outline'}
            size="sm"
            onClick={() => setExcludeTest(prev => !prev)}
            data-testid="report-excl-test"
            className="gap-1"
          >
            <FlaskConical className="h-3.5 w-3.5" />
            Excl. Test
          </Button>
          <span className="text-sm text-muted-foreground">
            {sortedSubs.length} of {submissions.length} record{submissions.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <ZohoReportViewManager
            formId={form.id}
            currentView={getCurrentView()}
            onLoadView={loadView}
          />
          <ZohoReportColumnPicker
            allFields={allFields}
            columns={columns}
            onColumnsChange={handleColumnsChange}
          />
          <ZohoReportExport
            submissions={sortedSubs}
            columns={columns}
            formName={form.form_name}
          />
          <Button variant="outline" size="sm" onClick={fetchSubmissions} data-testid="report-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <ZohoReportFilters
        allFields={allFields}
        filterFields={filterFields}
        onFilterFieldsChange={setFilterFields}
        submissions={submissions}
        filters={filters}
        onFiltersChange={setFilters}
      />

      {/* Table */}
      <ZohoReportTable
        submissions={sortedSubs}
        columns={columns}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={handleSort}
        form={form}
        onRefresh={fetchSubmissions}
      />
    </div>
  );
}
